// Football periods: one NFL week per period, one lineup window per period.
// Periods are derived from the real schedule so a Thursday game, a Saturday
// slate, or a rescheduled Monday game all fall where they should.

import type { Game, Period, Season, SeasonCalendar } from '@fsp/sports-core';

const DAY = 24 * 60 * 60 * 1000;
const REGULAR_SEASON_WEEKS = 18;

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

export function periodsFromGames(season: Season, games: readonly Game[]): Period[] {
  const byWeek = new Map<number, Game[]>();
  for (const g of games) {
    if (g.season !== season || g.periodHint === undefined) continue;
    if (g.status === 'canceled') continue;
    byWeek.set(g.periodHint, [...(byWeek.get(g.periodHint) ?? []), g]);
  }
  const weeks = [...byWeek.keys()].sort((a, b) => a - b);
  const periods: Period[] = [];
  const allTeams = new Set<string>();
  for (const g of games) {
    allTeams.add(g.homeTeam);
    allTeams.add(g.awayTeam);
  }

  weeks.forEach((week, i) => {
    const wg = byWeek.get(week)!;
    const starts = wg.map((g) => Date.parse(g.start)).sort((a, b) => a - b);
    const first = starts[0]!;
    const last = starts[starts.length - 1]!;
    const nextWeek = weeks[i + 1];
    const nextFirst = nextWeek
      ? Math.min(...byWeek.get(nextWeek)!.map((g) => Date.parse(g.start)))
      : null;
    // Start: previous period's end, or 3 days before this week's first kickoff.
    const start = i === 0 ? first - 3 * DAY : Date.parse(periods[i - 1]!.end);
    // End: half a day after the last kickoff (games are ~4h; leaves room for
    // final stats), but never past the next week's first kickoff.
    const end = nextFirst !== null ? Math.min(last + DAY / 2, nextFirst) : last + DAY / 2;
    const playing = new Set<string>();
    for (const g of wg) {
      playing.add(g.homeTeam);
      playing.add(g.awayTeam);
    }
    const proBye = [...allTeams].filter((t) => !playing.has(t)).sort();
    periods.push({
      season,
      index: i + 1,
      label: `Week ${week}`,
      start: iso(start),
      end: iso(end),
      windows: [
        { id: `${season}-w${String(week).padStart(2, '0')}`, start: iso(start), end: iso(end) },
      ],
      flags: { playoffEligible: week >= 15, proBye },
    });
  });
  return periods;
}

export const footballCalendar: SeasonCalendar = {
  periods(season, _lineupPeriod, games) {
    // Football only supports weekly lineups; the argument exists for the contract.
    return periodsFromGames(season, games);
  },
  defaultRegularSeasonPeriods() {
    return { first: 1, last: 14 };
  },
  defaultPlayoffPeriods() {
    return [15, 16, 17];
  },
  isInSeason(season, now) {
    // Early September through early February of the following year.
    const t = Date.parse(now);
    return t >= Date.UTC(season, 8, 1) && t < Date.UTC(season + 1, 1, 15);
  },
  // WEDNESDAY THROUGH SATURDAY, as ESPN offers them (Yahoo always runs
  // Wednesday): a week's last game is Monday night, and a Sunday or Monday run
  // would come before it (B-509).
  waiverWeekdays: [3, 4, 5, 6],
};

export { REGULAR_SEASON_WEEKS };
